import type { StoreStaticPages } from '@vastra/types';

const NBSP = '\u00A0';
/** Temporary sentinel (object replacement char) — not expected in CMS HTML. */
const WIDEN_GAP = '\uFFFC';

/**
 * Rich text from Quill often uses `&nbsp;` for ordinary word spaces, which prevents line wrapping
 * and breaks narrow layouts. Normalizes:
 * - Single NBSP (entity `&nbsp;`, `&#160;`, `&#xA0;`, or U+00A0) → normal ASCII space
 * - Runs of 2+ NBSP → ` \u00A0 ` (regular space, one NBSP, regular space) so lines can break at the
 *   outer spaces while keeping a modest non-breaking tie where extra width was intended
 */
export function normalizeStaticPageHtmlWhitespace(html: string): string {
  if (!html) return html;
  const unified = html
    .replace(/&nbsp;/gi, NBSP)
    .replace(/&#0*160;/gi, NBSP)
    .replace(/&#x0*A0;/gi, NBSP);
  return unified
    .replace(new RegExp(`${NBSP}{2,}`, 'g'), WIDEN_GAP)
    .replace(new RegExp(NBSP, 'g'), ' ')
    .split(WIDEN_GAP)
    .join(` ${NBSP} `);
}

/** Applies `normalizeStaticPageHtmlWhitespace` to every HTML/plain string field (admin + API before S3). */
export function normalizeStoreStaticPagesWhitespace(p: StoreStaticPages): StoreStaticPages {
  const n = normalizeStaticPageHtmlWhitespace;
  return {
    version: p.version,
    shipping: {
      pageTitle: n(p.shipping.pageTitle),
      introWhenRatesPublishedHtml: n(p.shipping.introWhenRatesPublishedHtml),
      introWhenRatesLoadingHtml: n(p.shipping.introWhenRatesLoadingHtml),
      introWhenRatesComingSoonHtml: n(p.shipping.introWhenRatesComingSoonHtml),
      ratesFootnoteHtml: n(p.shipping.ratesFootnoteHtml),
      orderProcessingHtml: n(p.shipping.orderProcessingHtml),
      returnsHtml: n(p.shipping.returnsHtml),
      helpHtml: n(p.shipping.helpHtml),
    },
    terms: {
      pageTitle: n(p.terms.pageTitle),
      lastUpdatedHtml: n(p.terms.lastUpdatedHtml),
      bodyHtml: n(p.terms.bodyHtml),
    },
    privacy: {
      pageTitle: n(p.privacy.pageTitle),
      lastUpdatedHtml: n(p.privacy.lastUpdatedHtml),
      bodyHtml: n(p.privacy.bodyHtml),
    },
    ...(p.updatedAt ? { updatedAt: p.updatedAt } : {}),
  };
}
